/* eslint-disable no-console */

import axios from "axios";
import { Dictionary } from "lodash";
import moment from "moment";
import React, {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  convertHeicToJpeg,
  getMimeType,
  isAudio,
  isCompressibleImage,
  isHeic,
  isImageThumbnailable,
  isVideoThumbnailable,
  getFileExtension,
  getFileKey,
} from "modules/utils/Functions";
import { useIndexDb } from "../index-db-provider";
import {
  FILE_STATE_TYPE,
  IFileInTransit,
} from "../index-db-provider/index-db-models";
import ObjectID from "bson-objectid";
import { useAuth } from "../auth-provider";
import { NEXT_PUBLIC_CLOUDFRONT_URL } from "app/client";
import { FILE_THUMB_CONTENT_TYPE } from "modules/utils/Constants";

// Used to distinguish "my in-progress uploads" from stale uploads left behind by a previous session or browser refresh.

const SESSION_ID = new ObjectID().toHexString();

interface IFileUpload {
  uploadFiles: (draftId: string, files: File[]) => void;
  replaceImage: (fileInTransit: IFileInTransit, file: File) => void;
  cancel: (id: string) => void;
  localFilesById: Dictionary<File>;
  removeLocalFiles: (ids: string[]) => void;
}

const FileContext = React.createContext<IFileUpload>({
  uploadFiles: () => null,
  replaceImage: () => null,
  cancel: () => null,
  localFilesById: {},
  removeLocalFiles: () => null,
});

export const FileProvider = ({ children }: PropsWithChildren) => {
  const { me } = useAuth();
  const { db } = useIndexDb();
  const abortControllersRef = useRef<Dictionary<AbortController>>({});
  const [localFiles, setLocalFiles] = useState<Dictionary<File>>({});

  const removeLocalFiles = useCallback((ids: string[]) => {
    setLocalFiles((prev) => {
      const next = { ...prev };
      ids.forEach((id) => delete next[id]);
      return next;
    });
  }, []);

  const safeModify = useCallback(
    async (
      id: string,
      modifier: (file: IFileInTransit) => void,
    ): Promise<IFileInTransit | null> => {
      const existing = await db.file.get(id);
      if (!existing) return null;
      const updated = { ...existing };
      modifier(updated);
      await db.file.where("id").equals(id).modify(modifier);
      return updated;
    },
    [db.file],
  );

  const setFileError = useCallback(
    (id: string) =>
      safeModify(id, (f) => {
        f.stateType = FILE_STATE_TYPE.ERROR;
      }),
    [safeModify],
  );

  const generateThumbnail = useCallback(
    async (fileInTransit: IFileInTransit, file: File) => {
      if (!me) return;

      try {
        const { generateImageThumbnail, generateVideoThumbnail, preloadImage } =
          await import("../../services/image/image-service");

        let blob: Blob | null = null;
        let previewExtension = fileInTransit.extension;

        if (isImageThumbnailable(fileInTransit.contentType)) {
          blob = await generateImageThumbnail(fileInTransit.id, file);
          previewExtension = "jpg";
        } else if (isVideoThumbnailable(fileInTransit.contentType)) {
          blob = await generateVideoThumbnail(fileInTransit.id, file);
          previewExtension = "jpg";
        }

        if (!blob) return;

        const localPreviewUrl = URL.createObjectURL(blob);

        await safeModify(fileInTransit.id, (dbFile) => {
          dbFile.localPreviewUrl = localPreviewUrl;
        });

        const previewKey = getFileKey(me.organizationId, previewExtension);
        const previewCloudFrontUrl = `${NEXT_PUBLIC_CLOUDFRONT_URL}/${previewKey}`;

        await axios.put(previewCloudFrontUrl, blob, {
          headers: { "Content-Type": FILE_THUMB_CONTENT_TYPE },
          withCredentials: true,
        });

        // Preload the CloudFront thumbnail before swapping; avoids flash
        await preloadImage(previewCloudFrontUrl);

        if (localPreviewUrl) {
          URL.revokeObjectURL(localPreviewUrl);
        }

        await safeModify(fileInTransit.id, (dbFile) => {
          dbFile.previewCloudFrontUrl = previewCloudFrontUrl;
          dbFile.previewKey = previewKey;
          dbFile.localPreviewUrl = null;
        });
      } catch (error) {
        console.warn(
          `Thumbnail generation failed for ${fileInTransit.id}:`,
          error,
        );
      }
    },
    [me, safeModify],
  );

  const uploadFileToS3 = useCallback(
    async (fileInTransit: IFileInTransit, file: File): Promise<void> => {
      const totalBytes = file.size;

      const stillExists = await safeModify(fileInTransit.id, (f) => {
        f.byteCount = totalBytes;
      });
      if (!stillExists) return;

      // Keep a local File reference for audio playback while uploading.
      if (isAudio(fileInTransit.contentType)) {
        setLocalFiles((prev) => ({
          ...prev,
          [fileInTransit.id]: file,
        }));
      }

      if (!fileInTransit.url) {
        await setFileError(fileInTransit.id);
        return;
      }

      const controller = new AbortController();
      abortControllersRef.current[fileInTransit.id] = controller;

      try {
        await axios.put(fileInTransit.url, file, {
          signal: controller.signal,
          headers: { "Content-Type": fileInTransit.contentType },
          withCredentials: true,
          onUploadProgress: async (progressEvent) => {
            await safeModify(fileInTransit.id, (f) => {
              f.updateTime = moment().toISOString();
              f.uploadedByteCount = progressEvent.loaded;
              f.percentage = (progressEvent.loaded / totalBytes) * 100;
            });
          },
        });

        // Only mark COMPLETE on actual success.
        await safeModify(fileInTransit.id, (f) => {
          f.stateType = FILE_STATE_TYPE.COMPLETE;
        });
      } catch (error) {
        if (axios.isCancel(error)) {
          // Cancelled by user — the cancel handler already cleaned up the DB record.
          return;
        }
        console.error(`Upload failed for ${fileInTransit.id}:`, error);
        await setFileError(fileInTransit.id);
      } finally {
        delete abortControllersRef.current[fileInTransit.id];
      }
    },
    [safeModify, setFileError],
  );

  const replaceImage = useCallback(
    async (fileInTransit: IFileInTransit, file: File) => {
      if (!me) return;

      const key = getFileKey(me.organizationId, fileInTransit.extension);
      const updatedFile = await safeModify(fileInTransit.id, (dbFile) => {
        dbFile.updateTime = moment().toISOString();
        dbFile.uploadedByteCount = 0;
        dbFile.percentage = 0;
        dbFile.byteCount = file.size;
        dbFile.stateType = FILE_STATE_TYPE.QUEUED;
        dbFile.key = key;
        dbFile.url = `${NEXT_PUBLIC_CLOUDFRONT_URL}/${key}`;
      });
      if (!updatedFile) return;

      // Await thumbnail before starting upload so DB writes don't interleave.
      await generateThumbnail(updatedFile, file);
      await uploadFileToS3(updatedFile, file);
    },
    [generateThumbnail, me, safeModify, uploadFileToS3],
  );

  const upload = useCallback(
    async (draftId: string, file: File) => {
      if (!me) return;

      let processedFile = file;
      let contentType = getMimeType(file);
      let fileName = file.name;

      // HEIC → JPEG conversion
      if (isHeic(contentType)) {
        try {
          processedFile = await convertHeicToJpeg(file);
          contentType = "image/jpeg";
          fileName = file.name.replace(/\.[^/.]+$/, ".jpeg");
        } catch (error) {
          console.error("HEIC conversion failed:", error);
          // Can't proceed without a usable file — bail out gracefully.
          return;
        }
      }

      const extension = getFileExtension(processedFile.name);
      const key = getFileKey(me.organizationId, extension);

      const fileInTransit: IFileInTransit = {
        id: new ObjectID().toHexString(),
        updateTime: moment().toISOString(),
        draftId,
        byteCount: processedFile.size,
        uploadedByteCount: 0,
        percentage: 0,
        name: fileName,
        extension,
        stateType: FILE_STATE_TYPE.QUEUED,
        contentType,
        isCopied: false,
        isCompressed: false,
        isVoiceNote: false,
        key,
        url: `${NEXT_PUBLIC_CLOUDFRONT_URL}/${key}`,
        sessionId: SESSION_ID,
      };

      // Persist immediately so the UI can show the file right away.
      await db.file.put(fileInTransit);

      // Await thumbnail so its DB write finishes before upload starts.
      await generateThumbnail(fileInTransit, processedFile);

      // Compress if applicable.
      if (isCompressibleImage(contentType, processedFile.size)) {
        try {
          const { compressImage } = await import(
            "../../services/image/image-service"
          );
          const compressedBlob = await compressImage(
            fileInTransit.id,
            processedFile,
          );
          if (compressedBlob) {
            processedFile = new File([compressedBlob], processedFile.name, {
              type: compressedBlob.type,
            });
            await safeModify(fileInTransit.id, (dbFile) => {
              dbFile.byteCount = processedFile.size;
              dbFile.isCompressed = true;
            });
          }
        } catch (error) {
          console.warn("Image compression failed, uploading original:", error);
          // Non-fatal — continue with the uncompressed file.
        }
      }

      await uploadFileToS3(fileInTransit, processedFile);
    },
    [me, db.file, generateThumbnail, safeModify, uploadFileToS3],
  );

  const uploadFiles = useCallback(
    (draftId: string, files: File[]) => {
      files.forEach((file) => {
        upload(draftId, file).catch((err) =>
          console.error("Unexpected upload error:", err),
        );
      });
    },
    [upload],
  );

  const cancelFileUpload = useCallback(
    async (id: string) => {
      // Abort first so the in-flight request stops before we delete the record.
      // This ordering prevents the upload's .then() from writing to a
      // now-deleted record.
      const controller = abortControllersRef.current[id];
      if (controller) {
        controller.abort("Upload cancelled by user");
        delete abortControllersRef.current[id];
      }

      const file = await db.file.get(id);

      if (file?.localPreviewUrl) {
        URL.revokeObjectURL(file.localPreviewUrl);
      }

      await db.file.delete(id);
      removeLocalFiles([id]);
    },
    [db.file, removeLocalFiles],
  );

  // If the user refreshes their browser, we need to remove any files that are in the process of being uploaded as the file pointers will be lost
  useEffect(() => {
    let cancelled = false;

    const cleanup = async () => {
      const files = await db.file.toArray();
      const staleIds = files
        .filter(
          (f) =>
            f.stateType !== FILE_STATE_TYPE.COMPLETE &&
            f.sessionId !== SESSION_ID,
        )
        .map((f) => f.id);

      if (!cancelled && staleIds.length > 0) {
        await db.file.bulkDelete(staleIds);
      }
    };

    cleanup().catch(console.error);

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fileUpload = useMemo<IFileUpload>(
    () => ({
      uploadFiles,
      replaceImage,
      cancel: cancelFileUpload,
      localFilesById: localFiles,
      removeLocalFiles,
    }),
    [cancelFileUpload, replaceImage, localFiles, removeLocalFiles, uploadFiles],
  );

  return (
    <FileContext.Provider value={fileUpload}>{children}</FileContext.Provider>
  );
};

export const useFile = () => useContext(FileContext);
